import type { PrototypeState } from './prototypeHydration';
import type { PrototypeRuntime, Root } from './prototypeTypes';

export const AI_CREATE_RESUME_REFRESH_WINDOW_MS = 30_000;
export const AI_CREATE_PROGRESS_REFRESH_INTERVAL_MS = 30_000;

type AiCreateFilter = 'inprogress' | 'completed';

export interface AiCreateProgressPollingState {
	activeCount: number;
	latestActiveCreatedAt: string | null;
	serverNow: string;
}

export type SettleAiCreateProgressPolling = (state?: AiCreateProgressPollingState | null) => void;

interface AiCreateResumeDependencies {
	now?: () => number;
	refreshWindowMs?: number;
	refresh: (
		runtime: PrototypeRuntime,
		root: Root,
		filter: AiCreateFilter,
		search: string,
	) => Promise<AiCreateProgressPollingState | null | void> | AiCreateProgressPollingState | null | void;
}

export interface AiCreateResumeController {
	destroy(): void;
}

interface RegisteredAiCreateResumeController extends AiCreateResumeController {
	setPollingState(state: AiCreateProgressPollingState | null): void;
	suspendPolling(): SettleAiCreateProgressPolling;
}

const controllers = new WeakMap<Root, RegisteredAiCreateResumeController>();

export function setAiCreateProgressPolling(root: Root, state: AiCreateProgressPollingState | null): void {
	controllers.get(root)?.setPollingState(state);
}

export function suspendAiCreateProgressPolling(root: Root): SettleAiCreateProgressPolling {
	return controllers.get(root)?.suspendPolling() ?? (() => undefined);
}

/** Owns the polling timer and stale-background refresh lifecycle for one root. */
export function createAiCreateResumeController(
	root: Root,
	state: PrototypeState,
	dependencies: AiCreateResumeDependencies,
): AiCreateResumeController {
	controllers.get(root)?.destroy();
	const doc = root instanceof Document ? root : root.ownerDocument;
	const win = doc.defaultView;
	const now = dependencies.now ?? (() => win?.performance.now() ?? Date.now());
	const refreshWindowMs = dependencies.refreshWindowMs ?? AI_CREATE_RESUME_REFRESH_WINDOW_MS;
	let hiddenAt: number | null = null;
	let destroyed = false;
	let pollingState: { metadata: AiCreateProgressPollingState; eligibleAt: number } | null = null;
	let pollingGeneration = 0;
	let suspended = false;
	let timer: number | null = null;
	let inFlight: Promise<void> | null = null;

	const clearTimer = () => {
		if (timer === null) return;
		win?.clearTimeout(timer);
		timer = null;
	};

	const currentFilter = (): AiCreateFilter => {
		const active = root.querySelector<HTMLElement>('#aiFilterTabs .category-pill.active');
		return active?.dataset.protoClick?.includes('inprogress') ? 'inprogress' : 'completed';
	};

	const resolvePollingState = (metadata: AiCreateProgressPollingState | null) => {
		if (!metadata || metadata.activeCount <= 0) return null;
		return { metadata, eligibleAt: now() };
	};

	const pollingBoundary = (): number | null => {
		return pollingState?.eligibleAt ?? null;
	};

	const canPoll = () => (
		!destroyed
		&& !suspended
		&& doc.visibilityState === 'visible'
		&& state.isLoggedIn
		&& Boolean(root.querySelector('#view-ai-create.active'))
		&& currentFilter() === 'inprogress'
		&& pollingBoundary() !== null
	);

	const isEligible = () => {
		const boundary = pollingBoundary();
		return boundary !== null && now() >= boundary;
	};

	const schedule = () => {
		if (!canPoll() || timer !== null || inFlight) return;
		const boundary = pollingBoundary();
		if (boundary === null) return;
		const delay = boundary > now() ? boundary - now() : AI_CREATE_PROGRESS_REFRESH_INTERVAL_MS;
		timer = win?.setTimeout(() => {
			timer = null;
			void requestRefresh();
		}, delay) ?? null;
	};

	const setPollingState = (nextState: AiCreateProgressPollingState | null) => {
		if (destroyed) return;
		pollingGeneration += 1;
		suspended = false;
		pollingState = resolvePollingState(nextState);
		clearTimer();
		schedule();
	};

	const suspendPolling = (): SettleAiCreateProgressPolling => {
		if (destroyed) return () => undefined;
		const generation = ++pollingGeneration;
		let settled = false;
		suspended = true;
		clearTimer();
		return (nextState) => {
			if (settled || destroyed || generation !== pollingGeneration) return;
			settled = true;
			suspended = false;
			if (nextState !== undefined) pollingState = resolvePollingState(nextState);
			schedule();
		};
	};

	const requestRefresh = (): Promise<void> | null => {
		if (!canPoll()) {
			clearTimer();
			return null;
		}
		if (!isEligible()) {
			schedule();
			return null;
		}
		if (inFlight) return inFlight;
		const search = root.querySelector<HTMLInputElement>('#aiSearchInput')?.value.trim() ?? '';
		const refreshGeneration = pollingGeneration;
		inFlight = Promise.resolve()
			.then(() => dependencies.refresh(state.runtime, root, 'inprogress', search))
			.then((nextState) => {
				if (nextState !== null && nextState !== undefined && refreshGeneration === pollingGeneration && !suspended) {
					pollingState = resolvePollingState(nextState);
				}
			})
			.catch(() => undefined)
			.finally(() => {
				inFlight = null;
				schedule();
			});
		return inFlight;
	};

	const resume = () => {
		if (destroyed || hiddenAt === null || doc.visibilityState !== 'visible') return;
		const elapsed = now() - hiddenAt;
		hiddenAt = null;
		if (!canPoll()) return;
		if (isEligible() && elapsed >= refreshWindowMs) {
			void requestRefresh();
			return;
		}
		schedule();
	};

	const onVisibility = () => {
		if (doc.visibilityState === 'hidden') {
			if (hiddenAt === null) hiddenAt = now();
			clearTimer();
			return;
		}
		resume();
	};
	const onBlur = () => {
		if (hiddenAt === null) hiddenAt = now();
		clearTimer();
	};
	const onFocus = () => resume();

	doc.addEventListener('visibilitychange', onVisibility);
	win?.addEventListener('blur', onBlur);
	win?.addEventListener('focus', onFocus);
	const controller: RegisteredAiCreateResumeController = {
		setPollingState,
		suspendPolling,
		destroy() {
			if (destroyed) return;
			destroyed = true;
			pollingGeneration += 1;
			suspended = false;
			pollingState = null;
			clearTimer();
			doc.removeEventListener('visibilitychange', onVisibility);
			win?.removeEventListener('blur', onBlur);
			win?.removeEventListener('focus', onFocus);
			if (controllers.get(root) === controller) controllers.delete(root);
		},
	};
	controllers.set(root, controller);
	return controller;
}
